import _ from 'lodash'

import { MENU_ITEM_PARAMETERS } from '@/constants/menu'
import { DONT_CALL_INTERCOM } from '@/constants/orderParameters'
import { ORDER_TYPES } from '@/constants/orderTypes'
import { PAYMENT_STATUSES } from '@/constants/paymentStatuses'
import { PAYMENT_TYPES } from '@/constants/paymentTypes'
import {
    STATUSES,
    ORDER_ALLOWED_TRANSACTION_ID_PAYMENT_TYPES,
    STAGES,
} from '@/constants/statuses'
import { TIME_TYPES } from '@/constants/timeTypes'
import { getUserData } from '@/core/auth/utils'
import { rawAddressWithoutCountry } from '@/store/utils/utils'
import useFormat from '@/views/utils'

import gettersContractors from './contractors/getters'

const { formatPhoneNumber, formatDate } = useFormat()

const formatInterval = interval => {
    if (!interval) return ''
    const { start_date_time, end_date_time } = interval

    const timezoneOffset = new Date().getTimezoneOffset()
    const startTime =
        new Date(start_date_time).getTime() - timezoneOffset * 60 * 1000
    const startDate = new Date(startTime)
        .toISOString()
        .split('T')[1]
        .slice(0, 5)
    const endTime =
        new Date(end_date_time).getTime() - timezoneOffset * 60 * 1000
    const endDate = new Date(endTime).toISOString().split('T')[1].slice(0, 5)

    return `${startDate} - ${endDate}`
}

const IMMEDIATE_SEND_STATUSES = [
    STATUSES.NEW,
    STATUSES.EDITING,
    STATUSES.DRAFT,
    STATUSES.SEND_ERROR,
]

export default {
    ...gettersContractors,
    price: state => {
        let price = 0
        if (state.order && state.order.dishes) {
            state.order.dishes.forEach(d => {
                price += +(d.count * d.price).toFixed(2)
            })
        }
        return price.toFixed(2)
    },

    getOrder: state => state.order,
    getGuestOrder: state => {
        if (!state.order || !state.order.guest) return null
        return {
            full_name: state.order.guest.first_name,
            first_name: state.order.guest.first_name,
            phone: state.order.phone,
            id: state.order.guest.id,
            label: formatPhoneNumber(state.order.phone),
        }
    },
    getGuestEmail: state => {
        if (
            !state.order ||
            !state.order.guest ||
            !state.order.guest.contacts ||
            !state.order.guest.contacts.length
        )
            return null

        return (
            state.order.guest.contacts.find(
                e => e.type_id === 'email' && e.is_default
            )?.value || null
        )
    },

    getOrderForEdit: state => {
        if (state.dirtiesAttributes.length === 0) return {}

        const order = {}
        const { order: orderData } = state
        const { time_type: timeType, order_type: orderType } = orderData
        const isOntimeDelivery =
            timeType === TIME_TYPES.ontime &&
            orderType?.id === ORDER_TYPES.DELIVERY

        // === Валидация ===
        if (
            (timeType === 'ontime' || timeType === 'interval') &&
            !orderData.delivered_date
        ) {
            order.error = 'Заполните Дата/Время доставки'
        }
        if (!orderData.brand_id) {
            order.error = 'Заполните Бренд'
        }

        // === Временные поля ===
        const timeFields = {
            claimed_cooking_time: isOntimeDelivery
                ? orderData.standard_cooking_time
                : orderData.cooking_time,
            claimed_over_time: orderData.over_time,
            claimed_delivery_time: isOntimeDelivery
                ? orderData.standard_delivery_time
                : orderData.delivery_time,
        }
        Object.entries(timeFields).forEach(([key, value]) => {
            if (state.dirtiesAttributes.includes(key)) {
                order.order_time = order.order_time || {}
                order.order_time[key] = value
            }
        })

        // === Простые поля (прямой маппинг) ===
        const simpleFields = {
            brand: 'brand_id',
            time_type: 'time_type',
            guest: 'guest_id',
            phone: 'phone',
            restaurant: 'division_id',
            kitchenNote: 'kitchen_note',
            addressNote: 'address_note',
            contractor: 'contractor',
            promos: 'promos',
        }
        Object.entries(simpleFields).forEach(([dirtyKey, orderKey]) => {
            if (state.dirtiesAttributes.includes(dirtyKey)) {
                order[orderKey] = orderData[orderKey]
            }
        })

        // delivered_date — с конвертацией в ISO
        if (
            state.dirtiesAttributes.includes('delivered_date') &&
            orderData.delivered_date
        ) {
            order.delivered_date = new Date(
                orderData.delivered_date
            ).toISOString()
        }

        // type — берём id из объекта order_type
        if (state.dirtiesAttributes.includes('type')) {
            order.type_id = orderType ? orderType.id : null
        }

        // sector — вложенный объект
        if (state.dirtiesAttributes.includes('sector')) {
            order.sector_id = orderData?.sector?.id || null
        }

        // === Адрес ===
        if (state.dirtiesAttributes.includes('address')) {
            order.address = {}
            const addr = orderData.address

            if (addr.id !== '00000000-0000-0000-0000-000000000000') {
                order.address.id = addr.id
            }

            // Простые строковые поля адреса
            ;[
                'raw_address',
                'settlement',
                'city',
                'street',
                'house',
                'flat',
                'floor',
                'entrance',
                'intercom',
            ].forEach(field => {
                if (state.dirtiesAttributes.includes(field)) {
                    order.address[field] = addr[field]
                }
            })

            // region — особый случай (region_id)
            if (state.dirtiesAttributes.includes('region')) {
                order.address.region_id = addr.region_id
            }

            // Координаты — с парсингом
            if (state.dirtiesAttributes.includes('longitude')) {
                order.address.longitude =
                    parseFloat(addr?.longitude?.toString().replace(',', '.')) ||
                    null
            }
            if (state.dirtiesAttributes.includes('latitude')) {
                order.address.latitude =
                    parseFloat(addr?.latitude?.toString().replace(',', '.')) ||
                    null
            }
        }

        // === Платежи ===
        const paymentDirtyKeys = [
            'first_payment',
            'main_transaction',
            'second_payment',
        ]
        if (
            paymentDirtyKeys.some(key =>
                state.dirtiesAttributes.includes(key)
            ) &&
            orderData.payments.length
        ) {
            order.payments = orderData.payments.map(payment => ({
                id: payment.id || null,
                payment_type_id: payment.payment_type_id ?? PAYMENT_TYPES.CASH,
                final_price: payment.final_price,
                change_from:
                    payment.payment_type_id === PAYMENT_TYPES.CASH
                        ? payment.change_from ?? 0
                        : null,
                transaction_id:
                    ORDER_ALLOWED_TRANSACTION_ID_PAYMENT_TYPES.includes(
                        payment.payment_type_id
                    )
                        ? payment.transaction_id
                        : null,
            }))
        }

        // === Параметры ===
        if (
            state.dirtiesAttributes.includes(`parameters.${DONT_CALL_INTERCOM}`)
        ) {
            const callIntercom = orderData.parameters?.find(
                v => v.name === DONT_CALL_INTERCOM
            )
            if (callIntercom) {
                order.parameters = [callIntercom]
            }
        }

        // === Блюда ===
        if (state.dirtiesAttributes.includes('dishes')) {
            const newDishes = orderData.dishes.filter(dish => !dish.id)

            const deletedDishes = orderData.dishes.filter(
                dish => dish.deleted_date
            )

            const editedDishes = state.dirtyDishes
                .map(id => orderData.dishes.find(dish => dish.id === id))
                .filter(Boolean)

            order.dishes = [
                ...newDishes,
                ...deletedDishes,
                ...editedDishes,
            ].map(el => {
                const dish = _.omit(el, ['temporary_id'])
                if (el.id) delete dish.modifiers
                return dish
            })
        }

        return order
    },
    getBrands: state => state.brands,
    clientOrderTypes: state =>
        state.types.filter(
            e => e.id === ORDER_TYPES.DELIVERY || e.id === ORDER_TYPES.SELF
        ),
    staffOrderTypes: state =>
        state.types.filter(
            e =>
                e.id === ORDER_TYPES.STAFF_OWN ||
                e.id === ORDER_TYPES.STAFF_OTHER
        ),
    isSelf: state => state.order.order_type.id === ORDER_TYPES.SELF,
    isDelivery: state => state.order.order_type.id === ORDER_TYPES.DELIVERY,
    isPreorder: state => state.order.order_type.id === ORDER_TYPES.PREORDER,
    isSubDelivery: state =>
        state.order.order_type.id === ORDER_TYPES.SUBDELIVERY,
    isStaffOwn: state => state.order.order_type?.id === ORDER_TYPES.STAFF_OWN,
    isStaffOther: state =>
        state.order.order_type?.id === ORDER_TYPES.STAFF_OTHER,
    isStaffOrder: (state, getters) =>
        getters.isStaffOwn || getters.isStaffOther,

    getRestaurants: state => state.restaurants,
    getBrandOrder: state =>
        state.brands.find(brand => brand.id === state.order.brand_id),
    getRegions: state => state.regions,
    getSectors: state => state.sectors,
    getOrderId: state => (state.order ? state.order.id : null),
    getDiscount: state =>
        state.order.promos.find(
            el => el.type === 'eds_discount' && !el.deleted_date
        ),
    getOnlinePaymentValue: state =>
        state.order?.payments?.filter(
            el => el.payment_type_id === PAYMENT_TYPES.ONLINE
        )[0]?.final_price || 0,
    getPromo: state => state.order.promos.find(el => el.type === 'promo'),
    getPromosAndDiscounts: state => state.order.promos,
    getPayments: state => {
        return (
            state.order.payments.map(payment => {
                return {
                    ...payment,
                    status_id: payment.status_id || PAYMENT_STATUSES.PENDING,
                }
            }) || []
        )
    },
    getBonuses: state =>
        state.order.promos.find(el => el.type === 'mindbox_fixed'),

    hasOrderPaymentTypeId: state => paymentTypeId => {
        return state.order.payments.some(
            payment => payment.payment_type_id === paymentTypeId
        )
    },

    hasOrderStatusId: state => statusId => {
        return state.order.payments?.some(
            payment => payment.status_id === statusId
        )
    },

    getYandexDeliveryPrice: state =>
        state.order.payments?.find(
            el => el.payment_type_id === PAYMENT_TYPES.YANDEX_DELIVERY
        ),
    getFirstPaymentType: state => {
        if (!state.order.payments) return null
        if (state.order.payments.length === 0) return null

        const payment = state.order.payments.find(
            el => el.payment_type_id !== PAYMENT_TYPES.YANDEX_DELIVERY
        )

        return {
            id: payment?.payment_type_id,
            name: payment?.type?.name,
        }
    },
    getAllDatesOrder: state => {
        return {
            createdDate: state.order.created_date,
            sendDate: state.order.send_date,
            deliveredDate: state.order.delivered_date,
            deliveredFactDate: state.order.delivered_fact_date,
            statusCancelDate: state.order.status_cancel_date,
            statusDoneDate: state.order.status_done_date,
            statusEditDate: state.order.status_edit_date,
        }
    },
    // TODO isolate
    getLastNotification: state => {
        if (!state.notifications || state.notifications.length === 0)
            return null
        const tempNotifications = structuredClone(state.notifications)

        tempNotifications.sort((a, b) => {
            return new Date(b.send_date) - new Date(a.send_date)
        })

        if (!tempNotifications[0] || !tempNotifications[0]?.send_date)
            return null

        return {
            label: `Последнее уведомление ${formatDate(
                tempNotifications[0].send_date
            )} (${tempNotifications[0].send_channel})`,
        }
    },
    getSendNotification: state => state.order.send_notification,

    getErrorsOrder: state => {
        if (
            !state.order ||
            !state.order.errors ||
            !state.order.errors.errors ||
            state.order.errors.errors.length === 0
        )
            return null
        return state.order.errors?.errors
            ?.map(error => {
                return error.error_text
            })
            .join(', ')
    },
    hasErrorsInOrder: state => {
        return !(
            !state.order ||
            !state.order.errors ||
            state.order.errors.length === 0
        )
    },
    isEmptyDish: state => {
        return !state.order.dishes || state.order.dishes.length === 0
    },
    hasErrorInDish: (state, getters) => {
        if (state.order.order_type.id === 'subdelivery') {
            return false
        }
        if (!state.order.dishes || state.order.dishes.length === 0) return true
        if (getters.getDishes.some(d => d.menu_item_id === null)) return true
        const result = []
        getters.getDishes.forEach(item => {
            const modifiersArr = item.modifiers || item.dish_modifiers || []
            if (modifiersArr && modifiersArr.length) {
                const error = modifiersArr.filter(
                    mod =>
                        !mod.modifier_id ||
                        mod.modifier_id ===
                            '00000000-0000-0000-0000-000000000000'
                )
                if (error.length) {
                    result.push(error)
                }
            }
        })

        return result.length > 0
    },

    getRecalc: state => {
        if (!state.order || !state.order.source) return false
        return state.order.source.recalc
    },

    getFinalPrice: state => {
        return (
            state.order.payments?.reduce(
                (acc, payment) => acc + payment.final_price,
                0
            ) || 0
        )
    },
    getDishesFinalPrice: state => {
        return (
            state.order.dishes?.reduce(
                (acc, dish) => acc + dish.final_price_with_topping * dish.count,
                0
            ) || 0
        )
    },
    getPrice: state => {
        return state.order.payments?.reduce((acc, payment) => {
            return acc + payment.price
        }, 0)
    },

    getBrandId: state => {
        return state.order.brand_id
    },

    getRkPriceTypeId: state => {
        return state.order.division_id && state.restaurants
            ? state.restaurants.find(e => e.id === state.order.division_id)
                  ?.rk_price_type_id
            : null
    },

    getParameter: state => {
        if (
            !state.order ||
            !state.order.order_type ||
            !state.order.order_type.id
        )
            return 'UNKNOWN'

        switch (state.order.order_type.id) {
            case ORDER_TYPES.SELF:
                return MENU_ITEM_PARAMETERS.IS_TAKEAWAY
            case ORDER_TYPES.PREORDER:
                return MENU_ITEM_PARAMETERS.IS_PREORDER
            case ORDER_TYPES.DELIVERY:
                return MENU_ITEM_PARAMETERS.IS_DELIVERY
            case ORDER_TYPES.STAFF_OWN:
                return MENU_ITEM_PARAMETERS.IS_STAFF_OWN
            case ORDER_TYPES.STAFF_OTHER:
                return MENU_ITEM_PARAMETERS.IS_STAFF_OTHER
            default:
                return 'UNKNOWN'
        }
    },
    getRegion: state => {
        return (
            state.regions.filter(
                c => c.id === state.order?.address?.region_id || null
            )[0] || {}
        )
    },
    getAddressRow: state => {
        if (!state.order.address) return ''

        const { street, house, raw_address } = state.order.address
        return street ? `${street}, ${house || ''}` : raw_address
    },
    getAddressToHumanString: state => {
        const callIntercom = state.order.parameters.find(
            v => v.name === DONT_CALL_INTERCOM
        )?.value

        const addCommaToString = value => (value ? `${value}, ` : '')

        const {
            city,
            street,
            house,
            longitude,
            latitude,
            flat,
            entrance,
            floor,
            settlement,
            raw_address,
        } = state.order.address

        if (!raw_address && city && street) {
            return `${addCommaToString(city)} ${addCommaToString(
                settlement
            )} ${addCommaToString(street)}
                д ${house || '-'},
                кв ${flat || '-'}, 
                подъезд: ${entrance || '-'}, 
                этаж: ${floor || '-'}, ${
                callIntercom !== 'true' ? '' : 'не звонить в домофон,'
            } 
                координаты: ${latitude || '-'} ${longitude || '-'}`
        }

        return `${rawAddressWithoutCountry(raw_address)},
            кв ${flat || '-'}, 
            подъезд: ${entrance || '-'}, 
            этаж: ${floor || '-'}, ${
            callIntercom !== 'true' ? '' : 'не звонить в домофон,'
        } 
            координаты: ${latitude || '-'} ${longitude || '-'}`
    },
    getInitCoordinates: state => state.initCoordinates,

    getErrorTime: state => {
        const type = state.order.order_type.id
        const cookingTime = state.order.cooking_time
        const deliveryTime = state.order.delivery_time
        const timeType = state.order.time_type

        if (timeType === TIME_TYPES.interval) {
            return ''
        }
        if (type === 'self') {
            if (!cookingTime) return 'Нет времени готовки'
        }
        if (type === 'delivery') {
            return (
                (!deliveryTime ? 'Нет времени доставки. ' : '') +
                (!cookingTime ? 'Нет времени готовки' : '')
            )
        }
        return ''
    },
    showClaimedTime: state => {
        if (state.order.send_date) return true
        return !!state.order.status_cancel_date
    },
    getSourceId: state => state.order.source.id,
    getOrderForAdd: state => {
        const { order } = state
        const statusId = order.status?.id ?? STATUSES.DRAFT

        const result = {
            status_id: statusId,
            time_type: order.time_type,
            brand_id: order.brand_id,
            division_id: order.division_id,
            guest_id: order.guest_id,
            source_id: order.source?.id ?? null,
            phone: order.phone,
            kitchen_note: order.kitchen_note,
            address_note: order.address_note,
            type_id: order.order_type?.id ?? null,
            created_by: 'api',
            operator_id: getUserData().user.id,
            payments: [],
        }

        if (result.time_type === 'ontime' && order.delivered_date) {
            result.delivered_date = new Date(order.delivered_date).toISOString()
        }

        if (order.address?.region_id) {
            result.address = {
                region_id: order.address.region?.id ?? null,
                street: order.address.street,
                ...(order.address.id && { id: order.address.id }),
                flat: order.address.flat,
                floor: order.address.floor,
                entrance: order.address.entrance,
                intercom: order.address.intercom,
            }
        }

        return result
    },

    prepareIntervalToFormat: () => intervals => {
        if (!intervals || intervals.length === 0) return null

        return {
            start_date_time: intervals[0]?.start_date_time,
            end_date_time: intervals[intervals.length - 1]?.end_date_time,
        }
    },

    getDeliveryInterval: (state, getters) => {
        if (state.deliveryIntervalsV2 && state.deliveryIntervalsV2.length > 0) {
            const sortedAppropriatedIntervals = state.deliveryIntervalsV2
                .filter(el => {
                    const { end_date_time } = el
                    const to = new Date(end_date_time).getTime()
                    const now = new Date().getTime()
                    return to >= now
                })
                .sort((a, b) => {
                    const { start_date_time: startA } = a
                    const { start_date_time: startB } = b
                    const toA = new Date(startA).getTime()
                    const toB = new Date(startB).getTime()
                    return toA - toB
                })

            const prepareIntervalToFormat = getters.prepareIntervalToFormat(
                sortedAppropriatedIntervals
            )

            return sortedAppropriatedIntervals
                ? formatInterval(prepareIntervalToFormat)
                : 'Cегодня нет интервалов'
        }

        return 'Нет свободных интервалов'
    },

    getPredictedDeliveryIntervals: state => {
        if (
            state.predictedDeliveryIntervals &&
            state.predictedDeliveryIntervals.length > 0
        ) {
            const firstDeliveryInterval = state.predictedDeliveryIntervals
                .filter(e => {
                    const { end_date_time } = e
                    const to = new Date(end_date_time).getTime()
                    const now = new Date().getTime()
                    return to >= now
                })
                .sort((a, b) => {
                    const { start_date_time: startA } = a
                    const { start_date_time: startB } = b
                    const toA = new Date(startA).getTime()
                    const toB = new Date(startB).getTime()
                    return toA - toB
                })
            return firstDeliveryInterval
                ? formatInterval(firstDeliveryInterval[0])
                : 'Cегодня нет интервалов'
        }
        return 'Нет доступных интервалов'
    },

    showPredictedDeliveryIntervals: state => {
        return !state.predictedDeliveryDate
    },

    getNearestInterval: (state, getters) => {
        // Не показывать интервалы если карточу заказа открыл оператор или заказ уже в работе или это Кулинария
        if (
            state.order.lock_by_user ||
            state.order.send_date ||
            state.order.status_cancel_date ||
            state.order.time_type === TIME_TYPES.interval
        ) {
            return {
                isVisible: false,
                text: '',
            }
        }

        const preText = 'Ближайший интервал:'
        const type = state.order.order_type.id

        if (type === ORDER_TYPES.SELF) {
            return {
                isVisible:
                    !state.isIntervalsAvailable &&
                    state.order.division_id != null,
                text: `${preText} ${getters.getDeliveryInterval}`,
            }
        }

        if (type === ORDER_TYPES.DELIVERY) {
            const hasAddress =
                !!state.order.address &&
                !!state.order.address.region_id &&
                !!state.order.address.raw_address

            return {
                isVisible: !state.isIntervalsAvailable && hasAddress,
                text: `${preText} ${getters.getDeliveryInterval}`,
            }
        }

        if (type === ORDER_TYPES.SUBDELIVERY) {
            return {
                isVisible: !state.isIntervalsAvailable,
                text: `${preText} ${getters.getDeliveryInterval}`,
            }
        }

        return {
            isVisible: false,
            text: '',
        }
    },

    showDeliveryIntervals: state => {
        // Не показывать интервалы если карточу заказа открыл оператор или заказ уже в работе или это Кулинария
        if (state.order.lock_by_user) return false
        if (state.order.send_date) return false
        if (state.order.status_cancel_date) return false
        if (state.order.time_type === TIME_TYPES.interval) return false

        // Не показывать интервалы если ресторан не выбран и это самовывоз
        if (!state.order.division_id && state.order.order_type.id === 'self')
            return false

        // На доставку - если нет адреса и нет доступного интервала
        // На самовывоз или довоз - если нет доступного интервала
        const type = state.order.order_type.id

        if (type === 'self' || type === 'subdelivery') {
            return !state.isIntervalsAvailable
        }

        if (type === 'delivery') {
            const hasAddress =
                !!state.order.address &&
                !!state.order.address.region_id &&
                !!state.order.address.raw_address

            return !state.isIntervalsAvailable && hasAddress
        }

        return false
    },

    hasTicketTime: state => {
        const type = state.order.order_type.id
        const cookingTime = state.order.cooking_time
        const deliveryTime = state.order.delivery_time

        const claimedCookingTime = state.order.order_time?.claimed_cooking_time
        const claimedDeliveryTime =
            state.order.order_time?.claimed_delivery_time

        const hasDishes = state.order.dishes && !!state.order.dishes.length

        const { time_type: timeType } = state.order
        // интервалы-кулинария
        if (timeType === TIME_TYPES.interval) {
            return true
        }

        // доставка
        if (type === ORDER_TYPES.DELIVERY)
            return (
                (!!cookingTime && !!deliveryTime) ||
                (!!claimedCookingTime && !!claimedDeliveryTime)
            )

        // самовывоз
        if ([ORDER_TYPES.SELF, ORDER_TYPES.PREORDER].includes(type))
            return !!cookingTime || !!claimedCookingTime

        // довоз
        if (type === ORDER_TYPES.SUBDELIVERY) {
            return (
                (!hasDishes && (!!deliveryTime || !!claimedDeliveryTime)) ||
                (hasDishes &&
                    ((!!cookingTime && !!deliveryTime) ||
                        (!!claimedCookingTime && !!claimedDeliveryTime)))
            )
        }

        return false
    },
    getReasonChangeTime: state => {
        if (!state.order.division_id) return ''

        const timeType = state.order.time_type
        if (timeType === TIME_TYPES.interval) return ''

        const cookingReason = state.reasonChanges.find(
            e => e.id === state.reasonChangeTime?.cooking_time_change_reason
        )
        const deliveryReason = state.reasonChanges.find(
            e => e.id === state.reasonChangeTime?.delivery_time_change_reason
        )
        const cookingComment = state.reasonChangeTime?.cooking_comment
        const deliveryComment = state.reasonChangeTime?.delivery_comment

        if (!cookingReason && !deliveryReason) {
            return ''
        }

        const cookingReasonText = cookingReason
            ? `Причина изменения времени приготовления: ${cookingReason?.name}${
                  cookingComment && ` | ${cookingComment}`
              }`
            : ''

        const deliveryReasonText = deliveryReason
            ? `Причина изменения времени доставки: ${deliveryReason?.name}${
                  deliveryComment && ` | ${deliveryComment}`
              }`
            : ''

        const type = state.order.order_type.id
        return type === 'self'
            ? cookingReasonText
            : [cookingReasonText, deliveryReasonText].filter(Boolean).join('\n')
    },

    isStatusCooking: state => {
        return (
            state.order.status?.name.toLowerCase() === 'начали готовить' ||
            false
        )
    },

    isStatusForbidden: state => forbiddenStatuses => {
        return forbiddenStatuses.includes(state.order.status?.id)
    },

    isOrderStatusToImmediateSend: state => {
        return IMMEDIATE_SEND_STATUSES.includes(state.order.status?.id)
    },

    isMinSumReached: (state, getters) => {
        const type = state.order.order_type.id

        // самовывоз
        if (type === ORDER_TYPES.SELF) return true
        if (!state.minSumOrder) return true

        return getters.getFinalPrice >= state.minSumOrder
    },
    checkRegion: state => {
        return !(
            !state.order.address.region_id &&
            (state.order.order_type.id === 'delivery' ||
                state.order.order_type.id === 'subdelivery')
        )
    },
    getTimeOrder: state => {
        const over =
            state.order.over_time ||
            state.order.order_time?.claimed_over_time ||
            0

        const delivery =
            state.order.delivery_time ||
            state.order.order_time?.claimed_delivery_time ||
            0

        const cook =
            state.order.cooking_time ||
            state.order.order_time?.claimed_cooking_time ||
            0

        return over + delivery + cook
    },
    getDishes: state => {
        return state.order.dishes?.filter(el => !el.deleted_date)
    },
    getDishesWithDeleted: state => {
        return state.order.dishes
    },
    getGuest: state => {
        if (!state.order || !state.order.guest)
            return {
                phone: '',
                first_name: '',
                full_name: '',
                id: '',
                is_blacklisted: false,
            }
        return {
            phone: state.order.phone,
            first_name: state.order.guest.first_name,
            full_name: state.order.guest.first_name,
            id: state.order.guest.id,
            is_blacklisted: state.order.guest.is_blacklisted,
        }
    },
    hasDishResend: state => {
        return state.resendDishes?.length > 0
    },
    getPredictedDeliveryDate: state => {
        return state.predictedDeliveryDate
    },
    getDeliveryDate: state => state.order.delivered_date,

    getSectorsForSelect: state => {
        if (!state.deliveryIntervalsV2?.length) return []

        const uniqueSectorIds = new Set(
            state.deliveryIntervalsV2.map(interval => interval.sector_id)
        )

        return Array.from(uniqueSectorIds)
            .map(sectorId => {
                const sector = state.sectors.find(s => s.id === sectorId)
                return sector ? { id: sector.id, name: sector.name } : null
            })
            .filter(Boolean)
    },

    getRestaurantsForSelect: state => {
        if (!state.order.sector_id) return []

        const restaurants = []
        state.deliveryIntervalsV2
            .filter(e => e.sector_id === state.order.sector_id)
            .forEach(e => {
                if (!restaurants.find(r => r.rk_id === e.restaurant_rk_id)) {
                    restaurants.push({
                        id: state.restaurants?.find(
                            r => r.rk_id === e.restaurant_rk_id
                        )?.id,
                        name:
                            state.restaurants?.find(
                                r => r.rk_id === e.restaurant_rk_id
                            )?.name || 'not found name',
                        rk_id: e.restaurant_rk_id,
                    })
                }
            })
        return restaurants
    },

    getIntervals: state => {
        if (!state.deliveryIntervalsV2) return []

        const restaurant = state.restaurants?.find(
            r => r.id === state.order.division_id
        )

        return state.deliveryIntervalsV2?.filter(e => {
            return (
                e.sector_id === state.order.sector_id &&
                e.restaurant_rk_id === restaurant?.rk_id
            )
        })
    },
    getIsRegionSelected: state => {
        return !!state.order.address.region_id
    },
    getOrderTypeId: state => state.order.order_type.id,
    getSecondPaymentTypes: (state, getters) => {
        const orderTypeId = getters.getOrderTypeId
        const mainPaymentType = getters.getFirstPaymentType
        const sourceId = state.order.source.id

        if (!mainPaymentType?.id) return []

        const paymentTypesByOrderType = state.paymentTypes.find(
            e => e.id === orderTypeId
        ).types[sourceId]

        const mainPaymentTypeInfo = paymentTypesByOrderType.find(
            el => el.payment_type_id === mainPaymentType.id
        )

        const secondPaymentTypes =
            mainPaymentTypeInfo?.paymentTypeBySecondPaymentTypeId

        return secondPaymentTypes || []
    },
    getSecondPaymentType: state => {
        if (!state.order.payments || state.order.payments?.length < 2)
            return null
        if (
            state.order.payments[1]?.payment_type_id ===
            PAYMENT_TYPES.YANDEX_DELIVERY
        )
            return null

        return state.order.payments[1].type
    },
    getIsSecondPaymentOnlineTransaction: state => {
        return (
            state.order.payments[1]?.payment_type_id ===
            PAYMENT_TYPES.ONLINE_TRANSACTION
        )
    },
    getSecondPayment: state => {
        if (!state.order.payments || state.order.payments?.length < 2)
            return null
        if (
            state.order.payments[1]?.payment_type_id ===
            PAYMENT_TYPES.YANDEX_DELIVERY
        )
            return null

        return state.order.payments[1]
    },
    getIsYandexSource: state => {
        return state.order.source.id === 'yandex'
    },
    getIsComboPayment: (state, getters) => {
        return getters.getSecondPayment && !getters.getIsYandexSource
    },
    getIsFirstPaymentPaid: state =>
        state.order.payments[0]?.status_id === PAYMENT_STATUSES.PAID,
    getIsFirstPaymentOnline: state =>
        state.order?.payments[0]?.payment_type_id === PAYMENT_TYPES.ONLINE,
    getIsFirstPaymentOnlineChange: state =>
        state.order?.payments[0]?.payment_type_id ===
        PAYMENT_TYPES.ONLINE_CHANGE,
    getIsFirstPaymentPreorder: state =>
        state.order?.payments[0]?.payment_type_id === PAYMENT_TYPES.PREORDER,
    getIsFirstPaymentUnderContract: state =>
        state.order?.payments[0]?.payment_type_id ===
        PAYMENT_TYPES.PAYMENT_UNDER_CONTRACT,
    getFirstPaymentTransactionId: state => {
        return state.order?.payments[0]?.transaction_id
    },
    getShowFirstPaymentTransactionId: state => {
        return (
            state.order?.payments[0]?.payment_type_id ===
                PAYMENT_TYPES.ONLINE ||
            state.order?.payments[0]?.payment_type_id ===
                PAYMENT_TYPES.INVOICE_BOX ||
            state.order?.payments[0]?.payment_type_id ===
                PAYMENT_TYPES.ONLINE_CHANGE ||
            state.order?.payments[0]?.payment_type_id ===
                PAYMENT_TYPES.SBP_COURIER ||
            state.order?.payments[0]?.payment_type_id === PAYMENT_TYPES.PREORDER
        )
    },
    getFirstPaymentFinalPrice: state => {
        return state.order?.payments[0]?.final_price || 0
    },
    getSecondPaymentTypeId: state => {
        return state.order?.payments[1]?.payment_type_id
    },
    getIsSecondPaymentCash: state => {
        if (state.order?.payments.length < 2) return false

        return state.order?.payments[1].payment_type_id === PAYMENT_TYPES.CASH
    },
    getIsSecondPaymentOnline: state => {
        return (
            state.order?.payments[1]?.payment_type_id === PAYMENT_TYPES.ONLINE
        )
    },
    getSecondPaymentTransactionId: state => {
        return state.order?.payments[1]?.transaction_id
    },
    getShowSecondPaymentTransactionId: state => {
        return (
            state.order?.payments[1]?.payment_type_id ===
                PAYMENT_TYPES.ONLINE ||
            state.order?.payments[1]?.payment_type_id ===
                PAYMENT_TYPES.INVOICE_BOX ||
            state.order?.payments[1]?.payment_type_id ===
                PAYMENT_TYPES.ONLINE_CHANGE
        )
    },
    getSecondPaymentChangeFrom: (state, getters) => {
        if (state.order?.payments.length < 2) return 0
        if (typeof state.order?.payments[1]?.change_from !== 'number') return 0

        const secondPayment = getters.getSecondPayment
        return secondPayment.change_from || 0
    },
    getSecondPaymentChange: state => {
        if (state.order?.payments.length < 2) return 0
        const firstPayment = state.order.payments[1]

        return (
            (firstPayment?.change_from || 0) - (firstPayment.final_price || 0)
        ).toFixed(0)
    },
    getSecondPaymentFinalPrice: state => {
        if (state.order?.payments.length < 2) return 0
        const secondPayment = state.order.payments[1]

        return secondPayment.final_price || 0
    },
    getFirstPaymentChangeFrom: state => {
        if (!state.order?.payments.length) return 0
        const firstPayment = state.order.payments[0]

        return firstPayment?.change_from || 0
    },
    getFirstPaymentChange: state => {
        if (!state.order?.payments.length) return 0
        const firstPayment = state.order.payments[0]

        return (
            (firstPayment?.change_from || 0) - (firstPayment.final_price || 0)
        ).toFixed(0)
    },
    getFinalPriceWithPromos: (state, getters) => {
        return (
            getters.getDishesFinalPrice -
            (getters.getBonuses?.discount || 0) -
            (getters.getPromo?.discount || 0)
        )
    },
    getHasErrorInFinalPrice: (state, getters) => {
        const somePaymentWithoutType = state.order.payments.some(
            payment => !payment.payment_type_id
        )

        if (somePaymentWithoutType) return false

        const hasMoreThanOnePayment = state.order.payments.length > 1

        if (!hasMoreThanOnePayment) return false

        const somePaymentHasZeroPrice = state.order.payments.some(
            payment => payment.final_price === 0
        )
        const somePaymentCoversAllDishesPrice = state.order.payments.some(
            payment => payment.final_price >= getters.getFinalPriceWithPromos
        )

        return somePaymentHasZeroPrice || somePaymentCoversAllDishesPrice
    },
    getShowSumInSecondPayment: state => {
        return state.order.payments.every(
            payment =>
                payment.payment_type_id &&
                payment.payment_type_id !== PAYMENT_TYPES.ONLINE_TRANSACTION
        )
    },
    getIsSubdelivery: state => {
        return state.order.type_id === 'subdelivery'
    },
    getIsFirstPaymentIdle: state => {
        return state.order.payments[0].payment_type_id === PAYMENT_TYPES.IDLE
    },
    getIsFirstPaymentChangeFromCorrect: (state, getters) => {
        if (getters.getFirstPaymentType.id !== PAYMENT_TYPES.CASH) return true
        if (getters.getFirstPaymentChangeFrom === 0) return true

        return (
            getters.getFirstPaymentChangeFrom >=
            getters.getFirstPaymentFinalPrice
        )
    },
    getIsSecondPaymentChangeFromCorrect: (state, getters) => {
        if (getters.getSecondPaymentTypeId !== PAYMENT_TYPES.CASH) return true
        if (getters.getSecondPaymentChangeFrom === 0) return true

        return (
            getters.getSecondPaymentChangeFrom >=
            getters.getSecondPaymentFinalPrice
        )
    },
    checkIsNoteExcessive: state => {
        return state.isNoteExcessive
    },

    getSecondPaymentAdditionalTransactionId: state =>
        state.order?.payments[1]?.transaction_id,

    getShowAdditionalTransactionId: state => {
        return (
            state.order?.payments[1]?.payment_type_id ===
                PAYMENT_TYPES.ONLINE_TRANSACTION ||
            state.order?.payments[1]?.payment_type_id === PAYMENT_TYPES.PREORDER
        )
    },

    getSecondPaymentStatusId: state => state.order?.payments[1]?.status_id,

    getFirstOriginalPaymentTypeId: state => {
        return state.originalOrderFirstPaymentType?.payment_type_id
    },

    getOriginalFirstPaymentTransactionId: state => {
        return state.originalOrderFirstPaymentType?.transaction_id
    },

    getOrderContractor: state => state.order?.contractor,

    getIsMainTransactionInvalid: state => state.isMainTransactionInvalid,

    hasOrderTwoPaidTransactions: (state, getters) =>
        getters.getPayments.length === 2 &&
        getters.getPayments.every(
            payment =>
                payment.status_id === PAYMENT_STATUSES.PAID &&
                Boolean(payment.transaction_id?.trim())
        ),

    unavailableStaffMenuDishes: (state, getters, rootState) => {
        if (!getters.isStaffOrder) {
            return []
        }

        const menuProducts = rootState.menu?.products || []
        if (!menuProducts.length) {
            return []
        }

        const dishes = state.order?.dishes
        if (!dishes?.length) {
            return []
        }

        const menuProductIds = new Set(menuProducts.map(p => p.id))

        return dishes.filter(
            item => !item.deleted_date && !menuProductIds.has(item.menu_item_id)
        )
    },
    isBeyondWaitingStage: state =>
        state.order.status?.stage > STAGES.WAITING_START_COOKING_OR_CANCELLED,
}
